import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { channelHasSubject, type NotificationChannel } from "../shared/communications-types";
import { parseSampleData } from "../shared/parse-sample-data";
import { useCommunicationVariables } from "./use-communication-variables";
import { renderTemplate } from "./render-template";

const PREVIEW_DEBOUNCE_MS = 250;

interface PreviewPanelProps {
  /** The communication owning this translation — drives the variable schema. */
  readonly communicationId: string;
  readonly channel: NotificationChannel;
  /** Live editor buffer — the preview reflects unsaved edits, rendered with
   *  the same Handlebars engine the server uses. */
  readonly subject: string;
  readonly body: string;
}

export function PreviewPanel({ communicationId, channel, subject, body }: PreviewPanelProps) {
  const { t } = useTranslation();
  const variables = useCommunicationVariables(communicationId);
  const hasSubject = channelHasSubject(channel);
  const isHtml = channel === "EMAIL";

  const [dataJson, setDataJson] = useState("{}");

  // Seed the sample-data editor from the backend's descriptor once it loads,
  // so the {{variables}} interpolate to real values without typing them blind.
  useEffect(() => {
    if (variables.data) {
      setDataJson(JSON.stringify(variables.data.sampleData ?? {}, null, 2));
    }
  }, [variables.data]);

  // Live render — recomputed on every keystroke (body / subject / data). No
  // button: HTML is rendered by the browser as the operator types. Parse +
  // render in one memo over primitive deps (an intermediate `data` object
  // would be a new reference each render and defeat the memo).
  const { dataInvalid, result } = useMemo(() => {
    const parsed = parseSampleData(dataJson);
    const invalid = "error" in parsed;
    return {
      dataInvalid: invalid,
      result: renderTemplate(
        { subject: hasSubject ? subject : null, body },
        invalid ? {} : parsed.data,
      ),
    };
  }, [dataJson, hasSubject, subject, body]);

  // Chromium does NOT reliably re-navigate an <iframe> when its `srcdoc` is
  // updated in place after creation — it keeps showing the document it first
  // loaded (here, the empty initial render before the editor seeds the body),
  // even though the attribute updates. So we remount the iframe via a changing
  // `key`, which creates a fresh element with the final content already set.
  // Debounced so typing doesn't thrash-remount (and re-fetch the email's fonts)
  // on every keystroke.
  const liveHtml = result.ok ? result.body : "";
  const previewHtml = useDebouncedValue(liveHtml, PREVIEW_DEBOUNCE_MS);

  return (
    <section
      data-testid="communication-preview-panel"
      className="space-y-3 rounded-md border border-border bg-card p-4"
    >
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">{t("communications.preview.heading")}</h4>
        <p className="text-xs text-muted-foreground">
          {isHtml
            ? t("communications.preview.liveHtmlHint")
            : t("communications.preview.liveTextHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="preview-data">{t("communications.preview.sampleData")}</Label>
        <Textarea
          id="preview-data"
          data-testid="communication-preview-data-input"
          value={dataJson}
          onChange={(e) => setDataJson(e.target.value)}
          rows={6}
          className="font-mono text-xs"
        />
        {variables.data && variables.data.variables.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t("communications.preview.knownVariables", {
              names: variables.data.variables.map((v) => v.name).join(", "),
            })}
          </p>
        )}
        {dataInvalid && (
          <p data-testid="communication-preview-json-error" className="text-sm text-destructive">
            {t("communications.preview.invalidJson")}
          </p>
        )}
      </div>

      {!result.ok ? (
        // Handlebars syntax error — surfaced with its message (incl. line).
        <div
          data-testid="communication-preview-template-error"
          className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3"
        >
          <p className="text-sm font-medium text-destructive">
            {t("communications.preview.templateError")}
          </p>
          <pre className="whitespace-pre-wrap break-words font-mono text-xs text-destructive">
            {result.error}
          </pre>
        </div>
      ) : (
        <div className="space-y-2">
          {hasSubject && (
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {t("communications.translations.subjectField")}
              </p>
              <p data-testid="communication-preview-subject" className="text-sm font-medium">
                {result.subject || "—"}
              </p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {t("communications.translations.bodyField")}
            </p>
            {isHtml ? (
              // Sandboxed (no allow-scripts) so any <script> in the body can't
              // run, and the email's own CSS is isolated from the app — same
              // as a mail client. Best-effort HTML: whatever the browser shows.
              <iframe
                key={previewHtml}
                data-testid="communication-preview-html"
                title={t("communications.preview.htmlFrameTitle")}
                sandbox=""
                srcDoc={previewHtml}
                className="h-80 w-full rounded-md border border-border bg-white"
              />
            ) : (
              <pre
                data-testid="communication-preview-body"
                className="whitespace-pre-wrap break-words rounded-md border border-border bg-background p-3 font-sans text-sm"
              >
                {result.body}
              </pre>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
